package com.daytimegaming.ourdailyfinances.presentation.dashboard

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.DashboardPreview
import com.daytimegaming.ourdailyfinances.domain.usecase.JoinDashboard
import com.daytimegaming.ourdailyfinances.domain.usecase.PreviewInviteCode
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class JoinDashboardState(
    val inviteCode: String = "",
    val isLoadingLookup: Boolean = false,
    val isLoadingJoin: Boolean = false,
    val error: String? = null,
    val preview: DashboardPreview? = null,
    val joinedDashboardId: String? = null
)

class JoinDashboardViewModel(
    private val previewInviteCodeUseCase: PreviewInviteCode,
    private val joinDashboardUseCase: JoinDashboard
) : ViewModel() {

    private val _state = MutableStateFlow(JoinDashboardState())
    val state = _state.asStateFlow()

    fun onInviteCodeChange(code: String) {
        _state.update { it.copy(inviteCode = code, error = null, preview = null) }
    }

    fun lookupInviteCode() {
        val code = _state.value.inviteCode
        if (code.isBlank()) {
            _state.update { it.copy(error = "Invite code cannot be empty") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isLoadingLookup = true, error = null, preview = null) }
            
            when (val response = previewInviteCodeUseCase(code)) {
                is Response.Success -> {
                    _state.update {
                        it.copy(
                            isLoadingLookup = false,
                            preview = response.data
                        )
                    }
                }
                is Response.Error -> {
                    _state.update {
                        it.copy(
                            isLoadingLookup = false,
                            error = response.message
                        )
                    }
                }
                else -> {
                    _state.update {
                        it.copy(
                            isLoadingLookup = false,
                            error = "Failed to look up invite code"
                        )
                    }
                }
            }
        }
    }

    fun joinDashboard(onSuccess: (String) -> Unit) {
        val code = _state.value.inviteCode
        if (code.isBlank()) {
            _state.update { it.copy(error = "Invite code cannot be empty") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isLoadingJoin = true, error = null) }
            when (val response = joinDashboardUseCase(code)) {
                is Response.Success -> {
                    _state.update { it.copy(isLoadingJoin = false, joinedDashboardId = response.data.dashboardId) }
                    onSuccess(response.data.dashboardId)
                }
                is Response.Error -> {
                    _state.update { it.copy(isLoadingJoin = false, error = response.message) }
                }
                else -> {
                    _state.update { it.copy(isLoadingJoin = false, error = "Unknown error occurred") }
                }
            }
        }
    }

    fun clearError() {
        _state.update { it.copy(error = null) }
    }
}
